import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiFilter,
  FiTrendingUp,
  FiArrowUp,
  FiArrowDown,
  FiUsers,
  FiShare2,
  FiSearch,
  FiRefreshCw,
  FiEdit2,
  FiAlertTriangle
} from 'react-icons/fi';

// Sports icons components
const BasketballIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-8c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2s-.9-2-2-2H9c-1.1 0-2 .9-2 2zm2-6c-1.1 0-2 .9-2 2h4c0-1.1-.9-2-2-2zm6 0c-1.1 0-2 .9-2 2h4c0-1.1-.9-2-2-2z"/>
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 3v18M3 12h18" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

const FootballIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <ellipse cx="12" cy="12" rx="9" ry="6" fill="none" stroke="currentColor" strokeWidth="1.5" transform="rotate(45 12 12)"/>
    <path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

const BaseballIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 3c-3 3-3 15 0 18M12 3c3 3 3 15 0 18" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="9" cy="10" r="1" fill="currentColor"/>
    <circle cx="15" cy="14" r="1" fill="currentColor"/>
  </svg>
);

const HockeyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <path d="M5 16h14v3H5z" fill="currentColor"/>
    <path d="M7 16V8c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="6" r="2" fill="currentColor"/>
    <path d="M4 17l2-1M20 17l-2-1" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const SoccerIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 3l3 5 5 2-2 5 2 5-5 2-3 5-3-5-5-2 2-5-2-5 5-2z" fill="none" stroke="currentColor" strokeWidth="1"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

const MmaIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <rect x="6" y="4" width="12" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="16" cy="16" r="1.5" fill="currentColor"/>
  </svg>
);
import { Button } from './Button';
import { Modal } from './Modal';
import { RovTooltip } from './RovTooltip';
import { AiScoreTooltip } from './AiScoreTooltip';
import { CustomNameModal } from './CustomNameModal';
import { CopyTradeConfigModal } from './CopyTradeConfigModal';
import { getTraderLeaderboard, createShareLink } from '../utils/api';
import { useI18n } from '../i18n/useI18n';
import { toDateSafe } from '../utils/datetime';
import { getTradingStyleBadgeClass } from '../utils/tradingStyle';

// 排名勋章组件
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return <span className="text-2xl md:text-3xl leading-none drop-shadow-md grayscale-[0.2] hover:grayscale-0 transition-all">🥇</span>;
  }
  if (rank === 2) {
    return <span className="text-2xl md:text-3xl leading-none drop-shadow-md grayscale-[0.2] hover:grayscale-0 transition-all">🥈</span>;
  }
  if (rank === 3) {
    return <span className="text-2xl md:text-3xl leading-none drop-shadow-md grayscale-[0.2] hover:grayscale-0 transition-all">🥉</span>;
  }
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-slate-800/50 border border-slate-700 text-slate-300 font-mono text-xs md:text-sm font-medium">
      {rank}
    </span>
  );
};

const formatUSD = (value) => {
  if (value === null || value === undefined) return '$0';
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) return `${value >= 0 ? '+' : '-'}$${(absValue / 1_000_000).toFixed(1)}M`;
  if (absValue >= 1_000) return `${value >= 0 ? '+' : '-'}$${(absValue / 1_000).toFixed(1)}k`;
  return `${value >= 0 ? '+$' : '-$'}${absValue.toFixed(0)}`;
};

const formatROV = (value) => {
  if (value === null || value === undefined) return '0%';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(1)}%`;
};

const formatAiScore = (value) => {
  if (value === null || value === undefined) return '--';
  return Number(value).toFixed(1);
};

const formatWalletAddress = (address) => {
  if (!address) return '--';
  return address.length > 10 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address;
};

const parseCount = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function TraderLeaderboardTable({
  onViewTrader = () => {},
  pushToast = () => {},
  isAuthenticated = false,
  onRequireLogin = () => {},
  sortByOverride = null,
  sortOrderOverride = null,
  lockSort = false
}) {
  const { t } = useI18n();
  const [sortBy, setSortBy] = useState(sortByOverride || 'rov');
  const [sortOrder, setSortOrder] = useState(sortOrderOverride || 'desc');
  const [activeCategory, setActiveCategory] = useState('sports');
  const [activeLeague, setActiveLeague] = useState(() => import.meta.env.VITE_LEADERBOARD_TAG || 'nba');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [portfolioCopyLoading, setPortfolioCopyLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [customNameTarget, setCustomNameTarget] = useState(null);
  const [botRiskState, setBotRiskState] = useState({ open: false, name: null, address: null, type: null, trader: null });
  const [copyConfigOpen, setCopyConfigOpen] = useState(false);
  const [copyConfigTrader, setCopyConfigTrader] = useState(null);
  const [portfolioRiskState, setPortfolioRiskState] = useState({
    open: false,
    riskItems: [],
    safeItems: [],
    allItems: [],
    sortBy: 'rov'
  });

  useEffect(() => {
    if (sortByOverride) {
      setSortBy(sortByOverride);
    }
  }, [sortByOverride]);

  useEffect(() => {
    if (sortOrderOverride) {
      setSortOrder(sortOrderOverride);
    }
  }, [sortOrderOverride]);

  const normalizeSuspiciousType = (value) => {
    if (!value) return 'none';
    return value === 'market-maker' ? 'market_maker' : value;
  };

  const getPortfolioDisplayName = (trader) => {
    const address = trader?.address;
    const shortenedAddress =
      address && address.length > 10 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address;
    return trader?.custom_name || trader?.username || shortenedAddress || address || '--';
  };

  const getSuspiciousLabel = (normalizedType) => {
    const suspiciousLabelMap = {
      robot: t('leaderboardTable.suspectedRobot'),
      market_maker: t('leaderboardTable.suspectedMarketMaker')
    };
    return suspiciousLabelMap[normalizedType];
  };

  const getSuspiciousBadgeClass = (normalizedType) => {
    if (normalizedType === 'robot') {
      return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
    }
    if (normalizedType === 'market_maker') {
      return 'border-rose-400/40 bg-rose-500/15 text-rose-200';
    }
    return 'border-slate-600/60 bg-slate-800/60 text-slate-300';
  };

  const executePortfolioCopy = (traderItems) => {
    const normalized = (traderItems || [])
      .map((item) => ({
        address: item?.address,
        name: item?.name,
      }))
      .filter((item) => item.address);
    if (!normalized.length) {
      pushToast(t('leaderboardTable.portfolioCopyNone'), 'info');
      return;
    }
    setPortfolioCopyLoading(true);
    openCopyConfigModal({
      mode: 'portfolio',
      traders: normalized,
    });
    setPortfolioCopyLoading(false);
  };

  const handlePortfolioCopy = async () => {
    try {
      if (!isAuthenticated) {
        pushToast(t('leaderboardTable.copyNeedLogin'), 'info');
        onRequireLogin();
        return;
      }

      const top10Traders = items.slice(0, 10);
      if (top10Traders.length === 0) {
        pushToast(t('leaderboardTable.portfolioCopyNone'), 'info');
        return;
      }
      const mapped = top10Traders.map((trader) => {
        const normalizedType = normalizeSuspiciousType(trader?.suspicious_type);
        const isSuspicious = ['robot', 'market_maker'].includes(normalizedType);
        const address = trader?.address;
        return {
          address,
          shortAddress: formatWalletAddress(address),
          name: getPortfolioDisplayName(trader),
          suspiciousType: normalizedType,
          suspiciousLabel: getSuspiciousLabel(normalizedType),
          suspiciousBadgeClass: getSuspiciousBadgeClass(normalizedType),
          isSuspicious
        };
      });

      const riskItems = mapped.filter((item) => item.isSuspicious);
      const safeItems = mapped.filter((item) => !item.isSuspicious);

      if (riskItems.length > 0) {
        setPortfolioRiskState({
          open: true,
          riskItems,
          safeItems,
          allItems: mapped,
          sortBy
        });
        return;
      }

      executePortfolioCopy(mapped);
    } catch (error) {
      console.error('[Portfolio copy error]', error);
      const errorMsg = error.message || t('leaderboardTable.followCreateFailed');
      pushToast(errorMsg, 'error');
    }
  };

  const handleOpenCustomName = useCallback(
    (trader) => {
      if (!isAuthenticated) {
        pushToast(t('customName.loginRequired'), 'info');
        onRequireLogin();
        return;
      }
      setCustomNameTarget(trader);
    },
    [isAuthenticated, onRequireLogin, pushToast, t]
  );

  const handleCustomNameSaved = useCallback(
    (customName) => {
      if (!customNameTarget?.address) return;
      const targetAddress = customNameTarget.address.toLowerCase();
      setItems((prev) =>
        prev.map((item) =>
          item.address?.toLowerCase() === targetAddress
            ? { ...item, custom_name: customName }
            : item
        )
      );
      setCustomNameTarget((prev) =>
        prev ? { ...prev, custom_name: customName } : prev
      );
    },
    [customNameTarget]
  );

  const handleShare = async () => {
    try {
      if (!isAuthenticated) {
        pushToast(t('leaderboardTable.shareNeedLogin'), 'info');
        onRequireLogin();
        return;
      }

      const response = await createShareLink({
        share_type: 'leaderboard',
        target_page: '/',
        target_params: {
          tag: activeLeague,
          sortBy: sortBy,
          sortOrder: sortOrder
        }
      });

      if (response?.code === 0 && response?.data?.share_url) {
        const baseUrl = window.location.origin;
        const fullShareUrl = `${baseUrl}${response.data.share_url}`;
        await navigator.clipboard.writeText(fullShareUrl);
        pushToast(t('leaderboardTable.shareCopied'), 'success');
      } else {
        pushToast(t('leaderboardTable.shareCreateFailed'), 'error');
      }
    } catch (error) {
      console.error('[Share link error]', error);
      pushToast(t('leaderboardTable.shareCreateFailed'), 'error');
    }
  };

  const openBotRiskModal = useCallback((payload) => {
    if (!payload?.address) return;
    setBotRiskState({
      open: true,
      name: payload.name || null,
      address: payload.address,
      type: payload.type || null,
      trader: payload.trader || null,
    });
  }, []);

  const closeBotRiskModal = useCallback(() => {
    setBotRiskState({ open: false, name: null, address: null, type: null, trader: null });
  }, []);

  const openCopyConfigModal = useCallback((traderInfo) => {
    setCopyConfigTrader(traderInfo);
    setCopyConfigOpen(true);
  }, []);

  const closeCopyConfigModal = useCallback(() => {
    setCopyConfigOpen(false);
  }, []);

  const closePortfolioRiskModal = useCallback(() => {
    setPortfolioRiskState({
      open: false,
      riskItems: [],
      safeItems: [],
      allItems: [],
      sortBy
    });
  }, [sortBy]);

  const handlePortfolioRiskConfirm = async (mode) => {
    if (portfolioCopyLoading) return;
    if (mode === 'safe') {
      const safeItems = portfolioRiskState.safeItems || [];
      if (!safeItems.length) {
        pushToast(t('leaderboardTable.portfolioRiskNoSafe'), 'info');
        return;
      }
      closePortfolioRiskModal();
      executePortfolioCopy(safeItems);
      return;
    }
    if (mode === 'all') {
      closePortfolioRiskModal();
      executePortfolioCopy(portfolioRiskState.allItems || []);
    }
  };

  const handleBotRiskConfirm = useCallback(() => {
    if (!botRiskState.address) return;
    closeBotRiskModal();
    const traderInfo = botRiskState.trader || {
      address: botRiskState.address,
      name: botRiskState.name || botRiskState.address,
    };
    openCopyConfigModal(traderInfo);
  }, [botRiskState.address, botRiskState.name, botRiskState.trader, closeBotRiskModal, openCopyConfigModal]);

  const fetchLeaderboard = useCallback(
    async () => {
      setLoading(true);
      try {
        const response = await getTraderLeaderboard({
          page: 1,
          pageSize: 100,
          sortBy,
          sortOrder,
          tag: activeLeague
        });
        const payload = response?.data || {};
        const incoming = payload.items || [];
        const limitedItems = incoming.slice(0, 100);
        setItems(limitedItems);
      } catch (error) {
        console.error('[API error]', error);
        pushToast(t('leaderboardTable.leaderboardLoadFailed'), 'info');
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [activeLeague, pushToast, sortBy, sortOrder, t]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sortByParam = urlParams.get('sortBy');
    if (sortByParam && ['rov', 'pnl', 'win_rate', 'trades', 'last_active', 'ai_score'].includes(sortByParam)) {
      setSortBy(sortByParam);
    }
    const sortOrderParam = urlParams.get('sortOrder');
    if (sortOrderParam === 'asc' || sortOrderParam === 'desc') {
      setSortOrder(sortOrderParam);
    }
    const refParam = urlParams.get('ref');
    if (refParam) {
      sessionStorage.setItem('share_ref_id', refParam);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const formatTimeAgo = useCallback(
    (input) => {
      const date = toDateSafe(input);
      if (!date) return '--';
      const diffMs = Math.max(0, now - date.getTime());
      const seconds = Math.floor(diffMs / 1000);
      if (seconds < 60) return t('leaderboardTable.secondsAgo', { n: seconds });
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return t('leaderboardTable.minutesAgo', { n: minutes });
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return t('leaderboardTable.hoursAgo', { n: hours });
      const days = Math.floor(hours / 24);
      return t('leaderboardTable.daysAgo', { n: days });
    },
    [now, t]
  );

  const categoryTabs = useMemo(
    () => [
      { key: 'sports', label: t('leaderboardTable.categorySports'), enabled: true },
      { key: 'politics', label: t('leaderboardTable.categoryPolitics'), enabled: false },
      { key: 'crypto', label: t('leaderboardTable.categoryCrypto'), enabled: false },
    ],
    [t]
  );

  const sportsLeagues = useMemo(
    () => [
      { key: 'nba', label: 'NBA', enabled: true, highlight: true, icon: BasketballIcon },
      { key: 'nfl', label: 'NFL', enabled: false, icon: FootballIcon },
      { key: 'mlb', label: 'MLB', enabled: false, icon: BaseballIcon },
      { key: 'nhl', label: 'NHL', enabled: false, icon: HockeyIcon },
      { key: 'soccer', label: t('leaderboardTable.leagueSoccer'), enabled: false, icon: SoccerIcon },
      { key: 'mma', label: 'MMA', enabled: false, icon: MmaIcon },
    ],
    [t]
  );


  const handleHeaderSort = (columnKey) => {
    if (lockSort) return;
    if (sortBy === columnKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortOrder('desc');
    }
  };

  const SortableHeader = ({ columnKey, children, align = 'left', className = '' }) => {
    const isActive = sortBy === columnKey;
    const textAlign = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
    const justifyAlign = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
    const iconSide = align === 'right' ? 'left-1' : 'right-1';
    const buttonPadding = align === 'right' ? 'pl-4' : align === 'center' ? 'px-4' : 'pr-4';

    if (lockSort) {
      return (
        <th className={`${textAlign} px-4 py-5 font-medium text-slate-400 whitespace-nowrap ${className}`}>
          <span className={`${isActive ? 'text-sky-300' : ''}`}>{children}</span>
        </th>
      );
    }

    return (
      <th className={`${textAlign} px-4 py-5 font-medium text-slate-400 whitespace-nowrap ${className}`}>
        <button
          type="button"
          className={`group relative flex w-full items-center ${justifyAlign} ${buttonPadding} gap-1.5 hover:text-white transition-colors ${
            isActive ? 'text-sky-400 font-semibold' : ''
          }`}
          onClick={() => handleHeaderSort(columnKey)}
        >
          <span className="relative z-10">{children}</span>
          <span
            className={`absolute ${iconSide} flex flex-col items-center transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
            }`}
          >
            {isActive ? (
              sortOrder === 'asc' ? <FiArrowUp className="text-xs" /> : <FiArrowDown className="text-xs" />
            ) : (
              <>
                <FiArrowUp className="text-[10px] leading-none" />
                <FiArrowDown className="text-[10px] leading-none" />
              </>
            )}
          </span>
        </button>
      </th>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Category Filter + Action Bar Combined */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-700/40 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-950/90 p-5 md:p-6 shadow-[0_8px_40px_rgba(2,6,23,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]">
        {/* Ambient glow effects */}
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-sky-500/5 blur-[100px]" />
        <div className="absolute -bottom-16 -left-12 h-48 w-48 rounded-full bg-indigo-500/5 blur-[80px]" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Left: Category Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {categoryTabs.map((category) => {
                const isActive = activeCategory === category.key;
                return (
                  <button
                    key={category.key}
                    type="button"
                    className={`group relative overflow-hidden px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-r from-sky-500/20 to-indigo-500/20 border-sky-500/40 text-sky-100'
                        : category.enabled
                          ? 'bg-slate-950/40 border-slate-700/40 text-slate-400 hover:border-slate-600/60 hover:text-slate-200 hover:bg-slate-900/40'
                          : 'bg-slate-950/20 border-slate-800/30 text-slate-600 cursor-not-allowed'
                    }`}
                    onClick={() => {
                      if (!category.enabled) return;
                      setActiveCategory(category.key);
                    }}
                  >
                    <span className="relative z-10 inline-flex items-center gap-1.5">
                      {category.label}
                      {!category.enabled && (
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">{t('leaderboardTable.soon')}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sports Leagues */}
            {activeCategory === 'sports' && (
              <div className="flex flex-wrap gap-2 pl-0 sm:pl-3 sm:border-l border-slate-700/30">
                {sportsLeagues.map((league) => {
                  const isActive = activeLeague === league.key;
                  const IconComponent = league.icon;
                  return (
                    <button
                      key={league.key}
                      type="button"
                      className={`group relative overflow-hidden px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-300 flex items-center gap-1.5 ${
                        isActive
                          ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/40 text-amber-100'
                          : league.enabled
                            ? 'bg-slate-950/40 border-slate-700/40 text-slate-400 hover:border-slate-600/60 hover:text-slate-200 hover:bg-slate-900/40'
                            : 'bg-slate-950/20 border-slate-800/30 text-slate-600 cursor-not-allowed'
                      }`}
                      onClick={() => {
                        if (!league.enabled) return;
                        setActiveLeague(league.key);
                      }}
                    >
                      <span className={isActive ? 'text-amber-300' : league.enabled ? 'text-slate-400 group-hover:text-slate-300' : 'text-slate-600'}>
                        <IconComponent />
                      </span>
                      <span className="relative z-10">{league.label}</span>
                      {!league.enabled && (
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">{t('leaderboardTable.soon')}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: Actions */}
          {items.length > 0 && (
            <div className="flex gap-2">
              <button
                disabled={loading}
                onClick={() => fetchLeaderboard()}
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-950/50 border border-slate-700/50 text-slate-300 text-xs font-medium transition-all duration-200 hover:bg-slate-800/50 hover:border-slate-600/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                {t('leaderboardTable.refresh')}
              </button>
              <button
                onClick={handleShare}
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-950/50 border border-slate-700/50 text-slate-300 text-xs font-medium transition-all duration-200 hover:bg-slate-800/50 hover:border-slate-600/50 hover:text-white"
              >
                <FiShare2 className="w-3.5 h-3.5" />
                {t('leaderboardTable.shareLeaderboard')}
              </button>
              <button
                disabled={portfolioCopyLoading || items.length === 0}
                onClick={handlePortfolioCopy}
                className="group flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-gradient-to-r from-sky-500/20 to-indigo-500/20 border border-sky-500/40 text-sky-100 text-xs font-medium transition-all duration-200 hover:from-sky-500/30 hover:to-indigo-500/30 hover:border-sky-500/60 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiTrendingUp className="w-3.5 h-3.5" />
                {portfolioCopyLoading
                  ? t('leaderboardTable.portfolioCopyLoading')
                  : t('leaderboardTable.portfolioCopyTop10')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-700/40 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-950/90 shadow-[0_8px_40px_rgba(2,6,23,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]">
        {/* Ambient glow effects */}
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-indigo-500/5 blur-[100px]" />
        <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-sky-500/5 blur-[100px]" />

        {initialLoading ? (
          <div className="relative z-10 flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 border-3 border-sky-500/20 border-t-sky-400 rounded-full animate-spin shadow-[0_0_20px_rgba(56,189,248,0.3)]" />
            <p className="text-slate-400 text-sm">{t('leaderboardTable.analyzing')}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="relative z-10 flex flex-col items-center justify-center py-20 gap-4">
             <div className="w-16 h-16 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
               <FiSearch className="text-3xl text-slate-600" />
             </div>
             <p className="text-slate-400">{t('leaderboardTable.noTraders')}</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="relative z-10 hidden lg:block overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-950/60 border-b border-slate-700/40">
                  <tr>
                    <th className="text-left px-6 py-5 font-medium text-slate-400 whitespace-nowrap w-20 text-[11px] uppercase tracking-wider">{t('leaderboardTable.rank')}</th>
                    <th className="text-left px-4 py-5 font-medium text-slate-400 whitespace-nowrap w-[220px] text-[11px] uppercase tracking-wider">{t('leaderboardTable.trader')}</th>
                    <SortableHeader columnKey="rov" align="right" className="w-24">
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider">
                        ROV
                        <RovTooltip />
                      </span>
                    </SortableHeader>
                    <SortableHeader columnKey="win_rate" align="right" className="w-24 text-[11px] uppercase tracking-wider">{t('leaderboardTable.winRate')}</SortableHeader>
                    <SortableHeader columnKey="pnl" align="right" className="w-28 text-[11px] uppercase tracking-wider">{t('leaderboardTable.totalProfit')}</SortableHeader>
                    <SortableHeader columnKey="trades" align="center" className="w-24 text-[11px] uppercase tracking-wider">{t('leaderboardTable.tradesCount')}</SortableHeader>
                    <SortableHeader columnKey="ai_score" align="right" className="w-24">
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider">
                        {t('leaderboardTable.aiScore')}
                        <AiScoreTooltip />
                      </span>
                    </SortableHeader>
                    <th className="text-left px-4 py-5 font-medium text-slate-400 whitespace-nowrap w-[160px] text-[11px] uppercase tracking-wider">
                      {t('leaderboardTable.tradingStyle')}
                    </th>
                    <SortableHeader columnKey="last_active" align="center" className="w-28 text-[11px] uppercase tracking-wider">
                      {t('leaderboardTable.lastActive')}
                    </SortableHeader>
                    <th className="text-center px-4 py-5 font-medium text-slate-400 whitespace-nowrap w-[140px] text-[11px] uppercase tracking-wider">{t('leaderboardTable.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40">
                  {items.map((trader) => {
                    const {
                      rank,
                      address,
                      username,
                      custom_name: customName,
                      win_rate: winRate,
                      total_profit: pnl,
                      total_positions: trades,
                      ai_score: aiScore,
                      trading_style: tradingStyle,
                      suspicious_type: suspiciousType,
                      last_active_at: lastActiveAt,
                      rov
                    } = trader;
                    const tradesCount = parseCount(trades);
                    const aiScoreValue = aiScore === null || aiScore === undefined ? null : Number(aiScore);
                    const aiScoreNumber = aiScoreValue ?? 0;
                    const normalizedSuspiciousType = suspiciousType === 'market-maker' ? 'market_maker' : suspiciousType;
                    const isAiSuspicious = ['robot', 'market_maker'].includes(normalizedSuspiciousType);
                    const suspiciousLabelMap = {
                      robot: t('leaderboardTable.suspectedRobot'),
                      market_maker: t('leaderboardTable.suspectedMarketMaker'),
                    };
                    const suspiciousLabel = suspiciousLabelMap[normalizedSuspiciousType];
                    const tradingStyleBadgeClass = getTradingStyleBadgeClass(tradingStyle);
                    const shortenedAddress =
                      address && address.length > 10 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address;
                    // Handle empty string username - check for truthy value properly
                    const effectiveUsername = username && username.trim() ? username : null;
                    const displayName = customName || effectiveUsername || shortenedAddress;

                    return (
                      <tr
                        key={`${rank}-${address}`}
                        className="group transition-all duration-200 hover:bg-slate-800/30"
                      >
                        {/* Rank */}
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-start">
                            <RankBadge rank={rank} />
                          </div>
                        </td>

                        {/* Trader */}
                        <td className="px-4 py-5 w-[280px] max-w-[280px]">
                          <div className="flex flex-col gap-1">
                            {/* Primary Name */}
                            <div className="inline-flex items-center gap-2 max-w-full">
                              <a
                                href={`/traders/${address}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group/name inline-flex items-center min-w-0"
                              >
                                <span
                                  className="font-semibold text-white group-hover/name:text-sky-400 transition-colors truncate text-sm"
                                  title={displayName}
                                >
                                  {displayName}
                                </span>
                              </a>
                              <button
                                type="button"
                                className="p-1 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-500 hover:bg-slate-700/50 transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleOpenCustomName(trader);
                                }}
                                title={t('customName.edit')}
                                aria-label={t('customName.edit')}
                              >
                                <FiEdit2 className="text-xs" />
                              </button>
                            </div>
                            {/* Address - Always show */}
                            <div className="text-xs text-slate-500 font-mono truncate max-w-[240px]" title={address}>
                              {shortenedAddress}
                            </div>
                          </div>
                        </td>

                        {/* ROV */}
                        <td className="px-4 py-5 text-right">
                          <div className={`font-mono font-medium ${rov >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {formatROV(rov)}
                          </div>
                        </td>

                        {/* Win Rate */}
                        <td className="px-4 py-5 text-right">
                          <div className="font-medium">
                            <span className={winRate >= 60 ? 'text-emerald-400' : winRate >= 50 ? 'text-amber-400' : 'text-slate-400'}>
                              {Number(winRate ?? 0).toFixed(1)}%
                            </span>
                          </div>
                        </td>

                        {/* PnL */}
                        <td className="px-4 py-5 text-right">
                          <div className={`font-mono font-medium ${pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {formatUSD(pnl || 0)}
                          </div>
                        </td>

                        {/* Trades */}
                        <td className="px-4 py-5 text-center">
                          <span className="inline-flex items-center justify-center min-w-[40px] px-2.5 py-1 rounded-lg bg-slate-800/50 text-slate-300 text-xs font-medium border border-slate-700/30">
                            {tradesCount}
                          </span>
                        </td>

                        {/* AI Score */}
                        <td className="px-4 py-5 text-right">
                          <span className={`inline-flex items-center justify-center min-w-[40px] font-mono font-semibold ${aiScoreNumber >= 80 ? 'text-emerald-400' : aiScoreNumber >= 60 ? 'text-amber-300' : 'text-slate-400'}`}>
                            {formatAiScore(aiScoreValue)}
                          </span>
                        </td>

                        {/* Trading Style */}
                        <td className="px-4 py-5 text-left">
                          {tradingStyle || (isAiSuspicious && suspiciousLabel) ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {tradingStyle && (
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap leading-none ${tradingStyleBadgeClass}`}>
                                  {tradingStyle}
                                </span>
                              )}
                              {isAiSuspicious && suspiciousLabel && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border border-amber-500/30 bg-amber-500/10 text-amber-300 whitespace-nowrap leading-none">
                                  {suspiciousLabel}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">--</span>
                          )}
                        </td>

                        {/* Last Active */}
                        <td className="px-4 py-5 text-center">
                          <span className="text-xs text-slate-500">
                            {formatTimeAgo(lastActiveAt)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-5 text-center min-w-[160px]">
                          <div className="flex items-center justify-center gap-2 flex-nowrap">
                            <button
                              onClick={() => {
                                if (!isAuthenticated) {
                                  pushToast(t('leaderboardTable.shareNeedLogin'), 'info');
                                  onRequireLogin();
                                  return;
                                }
                                const url = `${window.location.origin}/traders/${address}`;
                                navigator.clipboard.writeText(url);
                                pushToast(t('leaderboardTable.linkCopied'), 'success');
                              }}
                              title={t('leaderboardTable.share')}
                              className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-500 hover:bg-slate-700/50 transition-all shrink-0"
                            >
                              <FiShare2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                if (!isAuthenticated) {
                                  pushToast(t('leaderboardTable.copyNeedLogin'), 'info');
                                  onRequireLogin();
                                  return;
                                }
                                if (isAiSuspicious && suspiciousLabel) {
                                  openBotRiskModal({
                                    address,
                                    name: displayName || shortenedAddress || address,
                                    type: suspiciousLabel,
                                    trader: {
                                      address,
                                      name: displayName || shortenedAddress || address,
                                      winRate,
                                      totalProfit: pnl,
                                      aiScore: aiScoreValue,
                                      tradingStyle,
                                    },
                                  });
                                  return;
                                }
                                openCopyConfigModal({
                                  address,
                                  name: displayName || shortenedAddress || address,
                                  winRate,
                                  totalProfit: pnl,
                                  aiScore: aiScoreValue,
                                  tradingStyle,
                                });
                              }}
                              className="flex items-center justify-center px-3 h-8 rounded-lg bg-gradient-to-r from-sky-500/20 to-indigo-500/20 border border-sky-500/40 text-sky-100 text-xs font-medium hover:from-sky-500/30 hover:to-indigo-500/30 hover:border-sky-500/60 transition-all whitespace-nowrap shrink-0"
                            >
                              {t('leaderboardTable.follow')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden flex flex-col gap-3 p-4">
              {items.map((trader) => {
                const {
                  rank,
                  address,
                  username,
                  custom_name: customName,
                  win_rate: winRate,
                  total_profit: pnl,
                  total_positions: trades,
                  ai_score: aiScore,
                  trading_style: tradingStyle,
                  suspicious_type: suspiciousType,
                  last_active_at: lastActiveAt,
                  rov
                } = trader;
                const tradesCount = parseCount(trades);
                const aiScoreValue = aiScore === null || aiScore === undefined ? null : Number(aiScore);
                const aiScoreNumber = aiScoreValue ?? 0;
                const normalizedSuspiciousType = suspiciousType === 'market-maker' ? 'market_maker' : suspiciousType;
                const isAiSuspicious = ['robot', 'market_maker'].includes(normalizedSuspiciousType);
                const suspiciousLabelMap = {
                  robot: t('leaderboardTable.suspectedRobot'),
                  market_maker: t('leaderboardTable.suspectedMarketMaker'),
                };
                const suspiciousLabel = suspiciousLabelMap[normalizedSuspiciousType];
                const tradingStyleBadgeClass = getTradingStyleBadgeClass(tradingStyle);
                const shortenedAddress =
                  address && address.length > 10 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address;
                // Handle empty string username - check for truthy value properly
                const effectiveUsername = username && username.trim() ? username : null;
                const displayName = customName || effectiveUsername || shortenedAddress;

                return (
                  <div
                    key={`${rank}-${address}`}
                    className="glass-md p-4 active:scale-[0.99] transition-transform"
                  >
                    {/* Header: Rank + Name + Action */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-start gap-3 overflow-hidden">
                        <div className="shrink-0 mt-0.5">
                          <RankBadge rank={rank} />
                        </div>
                        <div className="min-w-0">
                          <a
                            href={`/traders/${address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-left font-bold text-white text-base truncate block w-full hover:text-sky-400 transition-colors mb-1"
                          >
                            {displayName}
                          </a>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="truncate max-w-[120px]">{shortenedAddress}</span>
                            {lastActiveAt && (
                              <span>{t('leaderboardSidebar.activeOn', { date: formatTimeAgo(lastActiveAt) })}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 shrink-0">
                         <button 
                           className="p-2 rounded-xl bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors"
                           onClick={() => {
                              const url = `${window.location.origin}/traders/${address}`;
                              navigator.clipboard.writeText(url);
                              pushToast(t('leaderboardTable.linkCopied'), 'success');
                           }}
                         >
                           <FiShare2 />
                         </button>
                         <button
                           className="p-2 rounded-xl bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors"
                           onClick={() => handleOpenCustomName(trader)}
                           title={t('customName.edit')}
                           aria-label={t('customName.edit')}
                         >
                           <FiEdit2 />
                         </button>
                         <Button
                           variant="primary"
                           size="small"
                           onClick={() => {
                                if (!isAuthenticated) {
                                  pushToast(t('leaderboardTable.copyNeedLogin'), 'info');
                                  onRequireLogin();
                                  return;
                                }
                                if (isAiSuspicious && suspiciousLabel) {
                                  openBotRiskModal({
                                    address,
                                    name: displayName || shortenedAddress || address,
                                    type: suspiciousLabel,
                                    trader: {
                                      address,
                                      name: displayName || shortenedAddress || address,
                                      winRate,
                                      totalProfit: pnl,
                                      aiScore: aiScoreValue,
                                      tradingStyle,
                                    },
                                  });
                                  return;
                                }
                                openCopyConfigModal({
                                  address,
                                  name: displayName || shortenedAddress || address,
                                  winRate,
                                  totalProfit: pnl,
                                  aiScore: aiScoreValue,
                                  tradingStyle,
                                });
                           }}
                         >
                           {t('leaderboardTable.follow')}
                         </Button>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="p-2.5 rounded-xl bg-slate-950/30 border border-slate-800/50 flex flex-col items-center justify-center gap-1">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider inline-flex items-center gap-0.5">
                          ROV
                          <RovTooltip iconClassName="text-slate-500 text-[8px]" />
                        </span>
                        <span className={`text-xs font-bold ${rov >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {formatROV(rov)}
                        </span>
                      </div>
                      <div className="p-2.5 rounded-xl bg-slate-950/30 border border-slate-800/50 flex flex-col items-center justify-center gap-1">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{t('leaderboardTable.winRate')}</span>
                        <span className={`text-xs font-bold ${winRate >= 60 ? 'text-emerald-400' : 'text-slate-200'}`}>
                          {Number(winRate ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="p-2.5 rounded-xl bg-slate-950/30 border border-slate-800/50 flex flex-col items-center justify-center gap-1">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{t('leaderboardTable.totalProfit')}</span>
                        <span className={`text-xs font-bold ${pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {formatUSD(pnl || 0)}
                        </span>
                      </div>
                      <div className="p-2.5 rounded-xl bg-slate-950/30 border border-slate-800/50 flex flex-col items-center justify-center gap-1">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{t('leaderboardTable.tradesCount')}</span>
                        <span className="text-xs font-bold text-slate-300">
                          {tradesCount}
                        </span>
                      </div>
                      <div className="p-2.5 rounded-xl bg-slate-950/30 border border-slate-800/50 flex flex-col items-center justify-center gap-1.5">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{t('leaderboardTable.tradingStyle')}</span>
                        {tradingStyle || (isAiSuspicious && suspiciousLabel) ? (
                          <div className="flex flex-wrap items-center justify-center gap-1">
                            {tradingStyle && (
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${tradingStyleBadgeClass}`}>
                                {tradingStyle}
                              </span>
                            )}
                            {isAiSuspicious && suspiciousLabel && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300">
                                {suspiciousLabel}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">--</span>
                        )}
                      </div>
                      <div className="p-2.5 rounded-xl bg-slate-950/30 border border-slate-800/50 flex flex-col items-center justify-center gap-1">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider inline-flex items-center gap-0.5">
                          {t('leaderboardTable.aiScore')}
                          <AiScoreTooltip iconClassName="text-slate-500 text-[8px]" />
                        </span>
                        <span className={`text-xs font-bold ${aiScoreNumber >= 80 ? 'text-emerald-400' : aiScoreNumber >= 60 ? 'text-amber-300' : 'text-slate-300'}`}>
                          {formatAiScore(aiScoreValue)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <Modal
        open={botRiskState.open}
        title={t('leaderboardTable.botRiskTitle')}
        subtitle={t('leaderboardTable.botRiskSubtitle')}
        onClose={closeBotRiskModal}
        maxWidthClass="max-w-[480px]"
      >
        <div className="flex flex-col items-center text-center py-2">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5">
            <FiAlertTriangle className="w-8 h-8 text-amber-400" />
          </div>

          <h3 className="text-lg font-semibold text-white mb-3">
            {t('leaderboardTable.botRiskConfirmTitle') || '确认继续跟单？'}
          </h3>

          <p className="text-slate-400 text-sm leading-relaxed mb-2 max-w-[360px]">
            {t('leaderboardTable.botRiskDesc', {
              name: botRiskState.name || '-',
              type: botRiskState.type || t('leaderboardTable.suspectedRobot'),
            })}
          </p>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent my-5" />

          <div className="flex gap-3 w-full">
            <Button
              variant="secondary"
              size="medium"
              onClick={closeBotRiskModal}
              className="flex-1"
            >
              {t('leaderboardTable.botRiskCancel')}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleBotRiskConfirm}
              className="flex-1"
            >
              {t('leaderboardTable.botRiskConfirm')}
            </Button>
          </div>
        </div>
      </Modal>

      <CopyTradeConfigModal
        open={copyConfigOpen}
        onClose={closeCopyConfigModal}
        trader={copyConfigTrader}
      />

      <Modal
        open={portfolioRiskState.open}
        title={t('leaderboardTable.portfolioRiskTitle')}
        subtitle={t('leaderboardTable.portfolioRiskSubtitle')}
        onClose={closePortfolioRiskModal}
        maxWidthClass="max-w-[520px]"
      >
        <div className="flex flex-col gap-5">
          {/* Warning Header */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0 shadow-[0_0_20px_rgba(245,158,11,0.15)]">
              <FiAlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-slate-100 mb-1.5">
                {t('leaderboardTable.portfolioRiskConfirmTitle')}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {t('leaderboardTable.portfolioRiskDesc', {
                  count: portfolioRiskState.riskItems.length
                })}
              </p>
            </div>
          </div>

          {/* Risk List */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-900/30 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 bg-slate-800/30">
              <span className="text-xs font-medium text-slate-400">
                {t('leaderboardTable.portfolioRiskListTitle')}
              </span>
              <span className="text-xs font-medium text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                {portfolioRiskState.riskItems.length}
              </span>
            </div>
            <div className="max-h-44 overflow-y-auto">
              {portfolioRiskState.riskItems.map((item, index) => (
                <div
                  key={item.address || item.name}
                  className={`flex items-center justify-between gap-3 px-4 py-3 ${index !== portfolioRiskState.riskItems.length - 1 ? 'border-b border-slate-700/30' : ''}`}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-slate-200 truncate">{item.name || '--'}</span>
                    <span className="text-xs text-slate-500 font-mono">{item.shortAddress}</span>
                  </div>
                  {item.suspiciousLabel ? (
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border shrink-0 ${item.suspiciousBadgeClass}`}
                    >
                      {item.suspiciousLabel}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2.5">
            <div className="flex gap-2.5">
              <Button
                variant="secondary"
                size="medium"
                onClick={closePortfolioRiskModal}
                className="flex-1"
                disabled={portfolioCopyLoading}
              >
                {t('leaderboardTable.portfolioRiskCancel')}
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={() => handlePortfolioRiskConfirm('safe')}
                className="flex-[1.2]"
                disabled={portfolioCopyLoading || portfolioRiskState.safeItems.length === 0}
              >
                {t('leaderboardTable.portfolioRiskFollowSafe')}
              </Button>
            </div>
            <Button
              variant="danger"
              size="medium"
              onClick={() => handlePortfolioRiskConfirm('all')}
              className="w-full"
              disabled={portfolioCopyLoading}
            >
              {t('leaderboardTable.portfolioRiskContinue')}
            </Button>
          </div>
        </div>
      </Modal>

      <CustomNameModal
        open={Boolean(customNameTarget)}
        onClose={() => setCustomNameTarget(null)}
        targetType="trader"
        targetId={customNameTarget?.address}
        currentName={customNameTarget?.custom_name || ''}
        displayName={
          customNameTarget
            ? customNameTarget.custom_name || customNameTarget.username || customNameTarget.address
            : ''
        }
        pushToast={pushToast}
        onSaved={handleCustomNameSaved}
      />
    </div>
  );
}
